import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import dayjs from "dayjs";
import { toast } from "sonner";
import { Loader2, Wrench, Siren } from "lucide-react";
import api, { apiError } from "../../lib/api";

const TYPES = ["Maintenance visit", "Vehicle swap", "Toll top-up", "Child seat request", "Registration renewal", "Other"];
const MODES = ["In-branch", "Mobile on-site"];

export const ServiceRequestsPanel = ({ bookings }) => {
  const usable = (bookings || []).filter((b) => b.status !== "cancelled");
  const [requests, setRequests] = useState(null);
  const [form, setForm] = useState({ booking_ref: usable[0]?.booking_ref || "", request_type: TYPES[0], service_mode: MODES[0], preferred_date: "", preferred_time: "", description: "" });
  const [photo, setPhoto] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/service-requests").then((r) => setRequests(r.data)).catch(() => setRequests([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/service-requests", form);
      if (photo) {
        const fd = new FormData();
        fd.append("file", photo);
        await api.post(`/service-requests/${data.request_id}/photo`, fd).catch(() => toast.warning("Request saved, but the photo failed to upload"));
      }
      toast.success(`Request ${data.request_id} received — we'll call within one business day`);
      setForm((f) => ({ ...f, description: "", preferred_date: "", preferred_time: "" }));
      setPhoto(null);
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  if (requests === null)
    return <div className="flex justify-center py-16"><Loader2 className="w-7 h-7 animate-spin text-[#009900]" /></div>;

  return (
    <div className="space-y-4" data-testid="service-requests-panel">
      <div className="bg-red-50 border border-red-100 rounded-2xl p-4 flex flex-wrap items-center justify-between gap-3" data-testid="sr-accident-link">
        <p className="text-sm text-red-800 flex items-center gap-2"><Siren className="w-4 h-4" /> Had an accident or breakdown? Call <strong>800 EUROPCAR</strong>, then file the report online.</p>
        <Link to="/report-accident" className="bg-red-600 text-white text-xs font-bold rounded px-4 py-2">Report an accident</Link>
      </div>
      <div className="grid lg:grid-cols-2 gap-6">
        <form onSubmit={submit} className="bg-white rounded-2xl border border-[#E2E8F0] p-5 space-y-4 h-fit" data-testid="service-request-form">
          <div>
            <h3 className="heading-upper text-sm tracking-widest text-[#144733] flex items-center gap-2"><Wrench className="w-4 h-4 text-[#009900]" /> New service request</h3>
            <p className="text-xs text-[#57685e] mt-1">Tell us what's going on — we'll take care of the rest.</p>
          </div>
          {usable.length === 0 ? (
            <p className="text-sm text-[#57685e]">Service requests are linked to a booking — book a car first.</p>
          ) : (
            <>
              <div>
                <label className="field-label">Booking *</label>
                <select className="field-input" value={form.booking_ref} onChange={(e) => setForm({ ...form, booking_ref: e.target.value })} data-testid="sr-booking-select">
                  {usable.map((b) => <option key={b.booking_ref} value={b.booking_ref}>{b.booking_ref} · {b.vehicle.name}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="field-label">Request type *</label>
                  <select className="field-input" value={form.request_type} onChange={(e) => setForm({ ...form, request_type: e.target.value })} data-testid="sr-type-select">
                    {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <label className="field-label">How should we serve you? *</label>
                  <select className="field-input" value={form.service_mode} onChange={(e) => setForm({ ...form, service_mode: e.target.value })} data-testid="sr-mode-select">
                    {MODES.map((m) => <option key={m} value={m}>{m}</option>)}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="field-label">Preferred date</label>
                  <input type="date" className="field-input" value={form.preferred_date} onChange={(e) => setForm({ ...form, preferred_date: e.target.value })} data-testid="sr-date-input" />
                </div>
                <div>
                  <label className="field-label">Preferred time</label>
                  <input type="time" className="field-input" value={form.preferred_time} onChange={(e) => setForm({ ...form, preferred_time: e.target.value })} data-testid="sr-time-input" />
                </div>
              </div>
              <div>
                <label className="field-label">Tell us what you need *</label>
                <textarea required rows={3} className="field-input" value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="sr-description-input" />
              </div>
              <div>
                <label className="field-label">Photo (optional — JPG/PNG/WEBP)</label>
                <input type="file" accept="image/jpeg,image/png,image/webp" className="field-input" onChange={(e) => setPhoto(e.target.files?.[0] || null)} data-testid="sr-photo-input" />
              </div>
              <button type="submit" disabled={busy} className="btn-cta rounded w-full py-3 text-xs" data-testid="sr-submit-btn">
                {busy ? "Sending…" : "Submit request"}
              </button>
            </>
          )}
        </form>

        <div className="space-y-3">
          {requests.length === 0 && <p className="text-sm text-[#57685e] bg-white border border-[#E2E8F0] rounded-2xl p-6" data-testid="sr-empty">No service requests yet.</p>}
          {requests.map((r) => (
            <div key={r.request_id} className="bg-white rounded-2xl border border-[#E2E8F0] p-4" data-testid={`sr-card-${r.request_id}`}>
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-mono font-bold text-[#144733] text-sm">{r.request_id}</span>
                <span className="text-[10px] uppercase tracking-wide font-semibold bg-[#e8f6ff] text-[#0369a1] rounded-full px-2 py-0.5">{r.status}</span>
                <span className="text-xs text-[#57685e] ml-auto">{dayjs(r.created_at).format("DD MMM, HH:mm")}</span>
              </div>
              <div className="text-sm font-semibold text-[#0F1E17] mt-1">{r.request_type} · {r.booking_ref}</div>
              <p className="text-xs text-[#57685e] mt-1">{r.description}</p>
              <p className="text-[10px] text-[#8a978f] mt-1.5">
                {r.service_mode || "In-branch"}{r.preferred_date ? ` · ${r.preferred_date}${r.preferred_time ? ` at ${r.preferred_time}` : ""}` : ""}{r.has_photo ? " · 1 photo attached" : ""}
              </p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
